import axios, { AxiosError, AxiosRequestConfig } from "axios";

export const API_URL = process.env.REACT_APP_API_URL ?? "https://api.mysterdead.xyz/v1";

export const NETWORK_ERROR_MESSAGE = "Nie udało się połączyć z serwerem.";

/** A failed API call: `message` is ready to show, `errors` lists the details when the API gave any. */
export class ApiError extends Error {
  readonly status: number;
  readonly errors?: string[];

  /** `status` is 0 when the server could not be reached. */
  constructor(message: string, status: number, errors?: string[]) {
    super(message);
    // Keeps `instanceof` working when the class is compiled down to ES5.
    Object.setPrototypeOf(this, ApiError.prototype);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  // A request that gets no answer ends as a lost connection (the error has no response), not as a page that loads forever.
  timeout: 15_000,
});

// The session lives in the MobX store, which imports this client, so the client
// cannot import the store back. The store (token, logout) and the router
// (onUnauthorized) register themselves here instead.
let getToken: () => string | null | undefined = () => null;
let logout: () => void = () => {};
let onUnauthorized: () => void = () => {};

export function setTokenGetter(fn: () => string | null | undefined): void {
  getToken = fn;
}

/** Called on a 401 for the current session, before `onUnauthorized`. */
export function setLogoutHandler(fn: () => void): void {
  logout = fn;
}

/** Called on a 401 for the current session, after the logout; the router sends the user to the login page. */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/** GET `path` and return the response body. */
export async function getData<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await api.get<T>(path, config);
  return data;
}

/** Query params without the unset ones (undefined, null and empty text). */
export function compactParams(params: object): Record<string, string | number | boolean> {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "");
  return Object.fromEntries(entries);
}

function messages(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : undefined;
}

/** Nest's `HttpException` body: `message` is a string or a list, `errors` is optional. */
function readBody(data: unknown): { message?: string; errors?: string[] } {
  if (typeof data !== "object" || data === null) return {};
  const { message, errors } = data as { message?: unknown; errors?: unknown };

  if (typeof message === "string" && message) return { message, errors: messages(errors) };
  const list = messages(message);
  if (list?.length) return { message: list.join(". "), errors: messages(errors) ?? list };
  return { errors: messages(errors) };
}

function toApiError(error: AxiosError): ApiError {
  if (!error.response) return new ApiError(NETWORK_ERROR_MESSAGE, 0);

  const { status, data } = error.response;
  const body = readBody(data);
  return new ApiError(body.message ?? `Serwer odpowiedział błędem (${status}).`, status, body.errors);
}

/**
 * A 401 means the session is over, but only for the token the request carried.
 * When several requests fail together, the first logs out and the rest find
 * their token already gone, so the user is sent to the login page once.
 */
function endSession(error: AxiosError): void {
  const sent = error.config?.headers?.get("Authorization");
  const token = getToken();
  if (!token || sent !== `Bearer ${token}`) return;

  logout();
  onUnauthorized();
}

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

api.interceptors.response.use(undefined, (error: unknown) => {
  // Cancellations and bugs in our own code are not API failures.
  if (!axios.isAxiosError(error) || axios.isCancel(error)) return Promise.reject(error);

  const apiError = toApiError(error);
  if (apiError.status === 401) endSession(error);
  return Promise.reject(apiError);
});
